import { inject, Injectable } from "@angular/core";
import { createEffect, EffectNotification, OnRunEffects } from "@ngrx/effects";
import {
    isStopLessonCommand,
    ITransferMinimumSlideInformationList,
    LessonHubFacade,
    reverseMapITransferMinimumSlideInformation,
} from "@pcp/streaming";
import { Observable } from "rxjs";
import { filter, map } from "rxjs/operators";
import { PUBLIC_BLOB_URL } from "shared/util-runtime-config";
import { classroomReceiverActions } from "../../public/store/classroom-sync.actions";
import { classroomConnectionActions } from "../../public/state/classroom.actions";
import {
    NavigationPreviewNextSlideData,
    NavigationPreviewNextSlidesTransferPayload,
} from "../../public/store/classroomState";
import { ClassroomFacade } from "../../public/yjs/classroom.facade";
import { PCP_LOGGER } from "@pcp/logging";

/**
 * Listens on Preview Screen changes
 * and transfers them to the classroom (beamer, students).
 */
@Injectable()
export class ClassroomConsumerEffects implements OnRunEffects {

    private readonly log = inject(PCP_LOGGER).createScoped('classroom');
    private readonly lessonHub = inject(LessonHubFacade);
    private readonly publicBlobUrl = inject(PUBLIC_BLOB_URL);
    private readonly lessonYjs = inject(ClassroomFacade);

    /**
     * Start sending command to the beamer only if a SignalR connection is established.
     * Stop sending commands when disconnected.
     */
    ngrxOnRunEffects(
        resolvedEffects$: Observable<EffectNotification>
    ): Observable<EffectNotification> {
        // NOTE: No need to add additional criteria to consumer events like
        // - isConnected -> we don't receive commands when not connected
        // - hasLessonId -> commands are sent to groups. When not connected to a lesson, the user has not joined the group
        return resolvedEffects$;
    }

    // NOTE: onPauseLessonReceived$ and onResumeLessonReceived$ REMOVED —
    // pause/resume state is now synced via Yjs.

    /**
     * Yjs navigation preview observer: deserializes `navigationPreviewBasicSlides` JSON
     * and dispatches to ClassroomState. Also rewrites relative thumbnail paths to absolute
     * URLs via `publicBlobUrl`.
     */
    readonly onYjsBasicSlidesChanged$ = createEffect(() =>
        this.lessonYjs.changes$.pipe(
            filter(changes => 'navigationPreviewBasicSlides' in changes),
            map(changes => {
                try {
                    const list: ITransferMinimumSlideInformationList =
                        JSON.parse(changes.navigationPreviewBasicSlides as string);
                    return classroomConnectionActions.setNavigationPreviewBasicSlides({
                        slides: reverseMapITransferMinimumSlideInformation(list, this.publicBlobUrl),
                        hasParent: list.hasParentSlide === true,
                    });
                } catch (err) {
                    this.log.error('Failed to parse navigationPreviewBasicSlides', err instanceof Error ? err : undefined, { err });
                    return null;
                }
            }),
            filter((action): action is NonNullable<typeof action> => action !== null),
        )
    );

    /**
     * Yjs navigation preview observer: deserializes `navigationPreviewNextSlides` JSON
     * and dispatches to ClassroomState. Also rewrites relative thumbnail paths to absolute
     * URLs via `publicBlobUrl`.
     */
    readonly onYjsNextSlidesChanged$ = createEffect(() =>
        this.lessonYjs.changes$.pipe(
            filter(changes => 'navigationPreviewNextSlides' in changes),
            map(changes => {
                try {
                    const raw: NavigationPreviewNextSlidesTransferPayload | null =
                        JSON.parse(changes.navigationPreviewNextSlides as string);
                    const data: NavigationPreviewNextSlideData | null = raw === null ? null : {
                        basicSlideIdx: raw.basicSlideIdx,
                        extensionSlideIdx: raw.extensionSlideIdx,
                        extensionSlides: reverseMapITransferMinimumSlideInformation(
                            raw.extensionSlideList, this.publicBlobUrl
                        ),
                        assignedDriverscamVideo: raw.assignedDriverscamVideo,
                    };
                    return classroomConnectionActions.setNavigationPreviewNextSlides({ data });
                } catch (err) {
                    this.log.error('Failed to parse navigationPreviewNextSlides', err instanceof Error ? err : undefined, { err });
                    return null;
                }
            }),
            filter((action): action is NonNullable<typeof action> => action !== null),
        )
    );

    readonly onStopLessonCommandReceived$ = createEffect(() =>
        this.lessonHub.Command.pipe(
            filter(isStopLessonCommand),
            map(() => classroomReceiverActions.receivedStopLessonCommand())
        )
    );


}
